import { action, makeObservable, observable } from 'mobx'

import {
  ArrowheadPosition,
  DEFAULT_MARKUP_STYLE,
} from '~/client/src/shared/components/MapMarkups/MarkupDrawController'
import type { IStylePreview } from '~/client/src/shared/components/MapMarkups/MarkupEditorStore'
import {
  deriveBorderColor,
  DRAW_DEFAULT_STYLE,
  HIGHLIGHTER_DEFAULT_STYLE,
  LINE_DEFAULT_STYLE,
  SHAPE_DEFAULT_STYLE,
} from '~/client/src/shared/components/MapMarkups/markupSegments'
import {
  ArrowheadType,
  DrawType,
  ERASER_THICKNESS,
  FillStyle,
  StrokeStyle,
} from '~/client/src/shared/components/MapMarkups/markupTools'

import type PhotoMarkupStore from './PhotoMarkup.store'
import {
  DEFAULT_TEXT_STYLE,
  ITextStyle,
  PHOTO_MARKUP_COLOR,
  PhotoMarkup,
} from './photoMarkupModel'

export default class PhotoMarkupStyle {
  @observable public color: string = PHOTO_MARKUP_COLOR
  @observable public strokeWidth: number = DEFAULT_MARKUP_STYLE.strokeWidth
  @observable public fillColor: string = SHAPE_DEFAULT_STYLE.fillColor
  @observable public fillStyle: FillStyle = 'solid'
  @observable public borderOverridden = false
  @observable.ref public textStyle: ITextStyle = DEFAULT_TEXT_STYLE
  private isPreviewing = false
  private previewBase: {
    markups: PhotoMarkup[]
    textStyle: ITextStyle
  } | null = null

  public constructor(private readonly store: PhotoMarkupStore) {
    makeObservable(this)
  }

  public get stylePreview(): IStylePreview {
    return {
      begin: this.startStylePreview,
      apply: this.applyStylePreview,
      cancel: this.cancelStylePreview,
    }
  }

  @action.bound
  public changeColor(color: string): void {
    this.color = color
    this.borderOverridden = true
  }

  @action.bound
  public changeStrokeWidth(width: number): void {
    this.strokeWidth = width
  }

  @action.bound
  public changeFillColor(fillColor: string): void {
    this.fillColor = fillColor
    const border = !this.borderOverridden && deriveBorderColor(fillColor)
    if (border) this.color = border
  }

  @action.bound
  public changeFillStyle(fillStyle: FillStyle): void {
    this.fillStyle = fillStyle
  }

  @action
  public applyShapeDefaults(): void {
    this.fillColor = SHAPE_DEFAULT_STYLE.fillColor
    this.fillStyle = 'solid'
    this.color = PHOTO_MARKUP_COLOR
    this.strokeWidth = SHAPE_DEFAULT_STYLE.strokeWidth
    this.borderOverridden = false
  }

  @action
  public applyLineDefaults(): void {
    this.color = PHOTO_MARKUP_COLOR
    this.strokeWidth = LINE_DEFAULT_STYLE.strokeWidth
  }

  @action
  public applyDrawTypeDefaults(type: DrawType): void {
    if (type === 'eraser') {
      this.strokeWidth = ERASER_THICKNESS
      return
    }
    const preset =
      type === 'highlighter' ? HIGHLIGHTER_DEFAULT_STYLE : DRAW_DEFAULT_STYLE
    this.color = PHOTO_MARKUP_COLOR
    this.strokeWidth = preset.strokeWidth
  }

  @action
  private updateSelected(patch: Partial<PhotoMarkup>): void {
    const selected = this.store.selected
    if (!selected) return
    this.store.replaceMarkup({ ...selected, ...patch } as PhotoMarkup)
    if (!this.isPreviewing) this.store.history.capture()
  }

  @action.bound
  public changeSelectedColor(color: string): void {
    this.updateSelected({ color, borderOverridden: true })
  }

  @action.bound
  public changeSelectedWidth(strokeWidth: number): void {
    this.updateSelected({ strokeWidth })
  }

  @action.bound
  public changeSelectedStrokeStyle(strokeStyle: StrokeStyle): void {
    this.updateSelected({ strokeStyle })
  }

  @action.bound
  public changeSelectedArrowhead(
    position: ArrowheadPosition,
    type: ArrowheadType,
  ): void {
    this.updateSelected(
      position === 'start' ? { arrowheadStart: type } : { arrowheadEnd: type },
    )
  }

  @action.bound
  public changeSelectedFillColor(fillColor: string): void {
    const selected = this.store.selectedPath
    if (!selected) return
    const border = !selected.borderOverridden && deriveBorderColor(fillColor)
    this.updateSelected(border ? { fillColor, color: border } : { fillColor })
  }

  @action.bound
  public changeSelectedFillStyle(fillStyle: FillStyle): void {
    this.updateSelected({ fillStyle })
  }

  @action
  private updateText(patch: Partial<ITextStyle>): void {
    this.textStyle = { ...this.textStyle, ...patch }
    this.updateSelected(patch)
  }

  private get currentText(): ITextStyle {
    return this.store.selectedText ?? this.textStyle
  }

  @action.bound
  public changeTextColor(color: string): void {
    this.updateText({ color })
  }

  @action.bound
  public changeTextFillColor(fillColor: string): void {
    this.updateText({ fillColor })
  }

  @action.bound
  public changeTextFontSize(fontSize: number): void {
    this.updateText({ fontSize })
  }

  @action.bound
  public toggleTextBold(): void {
    this.updateText({ bold: !this.currentText.bold })
  }

  @action.bound
  public toggleTextItalic(): void {
    this.updateText({ italic: !this.currentText.italic })
  }

  @action.bound
  public toggleTextUnderline(): void {
    this.updateText({ underline: !this.currentText.underline })
  }

  @action.bound
  private startStylePreview(): void {
    this.isPreviewing = true
    this.previewBase = this.store.selected
      ? { markups: this.store.markups, textStyle: this.textStyle }
      : null
  }

  @action.bound
  private applyStylePreview(): void {
    this.isPreviewing = false
    this.previewBase = null
    this.store.history.capture()
  }

  @action.bound
  private cancelStylePreview(): void {
    if (this.previewBase) {
      this.store.setMarkups(this.previewBase.markups)
      this.textStyle = this.previewBase.textStyle
    }
    this.isPreviewing = false
    this.previewBase = null
  }
}
